import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useQueryClient,
} from '@tanstack/react-query';
import {
  isNilOrZero,
  notEmptyString,
} from '@populateui/utils';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import {
  useNotify,
} from 'core/hooks';
import {
  EnrollPatientInPCCProgramsRequestDTO,
} from 'dtos';
import {
  PCCProgram,
} from 'dtos/pCCProgram';
import {
  pccPatientStatusQueryKey,
  useEnrollPatientInPCC,
} from 'views/EMR/hooks/pcc';
import {
  EnrollStep,
  getEnrollSteps,
  selectionIncludesRTM,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/EnrollDialog/steps';
import {
  PCCEncounterValue,
  PCCEnrollParams,
  PCCEnrollResolvedParams,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/EnrollDialog/types';
import {
  patientAlertsEventChannel,
  pccEventChannel,
} from 'pages/Dashboard/services/eventChannels';

type UseEnrollFlowResult = {
  step: EnrollStep;
  selectedPrograms: PCCProgram[];
  planText: string;
  encounterValue: PCCEncounterValue | null;
  isEnrolling: boolean;
  setSelectedPrograms: (programs: PCCProgram[]) => void;
  setPlanText: (plan: string) => void;
  setEncounterValue: (value: PCCEncounterValue | null) => void;
  goBack: () => void;
  continueFromPrograms: () => Promise<boolean>;
  approvePlan: () => void;
  completeAttestation: () => Promise<void>;
  skip: () => void;
};

function emitResolved(params: PCCEnrollResolvedParams) {
  pccEventChannel.emit('onPCCEnrollResolved', params);
}

function serializeEncounter(
  lockedEncounterId: number | undefined,
  value: PCCEncounterValue | null,
): Pick<EnrollPatientInPCCProgramsRequestDTO, 'encounterId' | 'newAppointment'> {
  return !isNilOrZero(lockedEncounterId)
  ? { encounterId: lockedEncounterId }
  : value ?? {};
}

export default function useEnrollFlow(context: PCCEnrollParams | null): UseEnrollFlowResult {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { mutateAsync: enrollPatient, isPending: isEnrolling } = useEnrollPatientInPCC();
  const [selectedPrograms, setSelectedPrograms] = useState<PCCProgram[]>([]);
  const [planText, setPlanText] = useState('');
  const [encounterValue, setEncounterValue] = useState<PCCEncounterValue | null>(null);
  const [stepIndex, setStepIndex] = useState(0);

  const steps = useMemo(
    () => (isNil(context)
      ? []
      : getEnrollSteps(context.variant, selectionIncludesRTM(selectedPrograms))),
    [context, selectedPrograms],
  );

  const step = steps[stepIndex] ?? 'programs';

  useEffect(() => {
    if (isNil(context)) {
      setSelectedPrograms([]);
      setPlanText('');
      setEncounterValue(null);
      setStepIndex(0);
      return;
    }

    setSelectedPrograms(context.selectedPrograms);
    setPlanText('');
    setEncounterValue(null);
    setStepIndex(0);
  }, [context]);

  const resolve = useCallback((
    enrolled: boolean,
    plan: string | null,
    programs: PCCProgram[],
  ) => {
    if (isNil(context)) {
      return;
    }

    emitResolved({
      visitNoteId: context.visitNoteId,
      enrolled,
      plan,
      programs,
      variant: context.variant,
      payload: context.payload,
    });
  }, [context]);

  const enrollSelected = useCallback(async (
    programs: PCCProgram[],
    plan: string | null,
  ) => {
    if (isNil(context) || programs.length === 0) {
      resolve(false, null, []);
      return;
    }

    const includesRTM = selectionIncludesRTM(programs);
    const encounterTarget = serializeEncounter(context.encounterId, encounterValue);
    if (includesRTM && isEmpty(encounterTarget)) {
      return;
    }

    await enrollPatient({
      patientId: context.patientId,
      programs,
      ...encounterTarget,
      ...(includesRTM && notEmptyString(plan) ? { rtmPlan: plan } : {}),
    });

    await queryClient.invalidateQueries({
      queryKey: pccPatientStatusQueryKey(context.patientId),
    });

    notify({
      variant: 'success',
      message: programs.length > 1
        ? 'Patient enrolled in selected PCC programs'
        : `Patient enrolled in ${programs[0]}`,
    });

    patientAlertsEventChannel.emit('onPatientAlertsChanged', context.patientId);
    resolve(true, plan, programs);
  }, [context, encounterValue, enrollPatient, notify, queryClient, resolve]);

  const skip = useCallback(() => {
    resolve(false, null, []);
  }, [resolve]);

  const goBack = useCallback(() => {
    setStepIndex((current) => Math.max(0, current - 1));
  }, []);

  const continueFromPrograms = useCallback(async () => {
    if (selectionIncludesRTM(selectedPrograms)) {
      setStepIndex(1);
      return false;
    }

    await enrollSelected(selectedPrograms, null);
    return true;
  }, [enrollSelected, selectedPrograms]);

  const approvePlan = useCallback(() => {
    setStepIndex((current) => current + 1);
  }, []);

  const completeAttestation = useCallback(async () => {
    await enrollSelected(selectedPrograms, planText);
  }, [enrollSelected, planText, selectedPrograms]);

  return {
    step,
    selectedPrograms,
    planText,
    encounterValue,
    isEnrolling,
    setSelectedPrograms,
    setPlanText,
    setEncounterValue,
    goBack,
    continueFromPrograms,
    approvePlan,
    completeAttestation,
    skip,
  };
}
